/**
 * Utility for retrying asynchronous operations with exponential backoff and jitter.
 */

export interface RetryOptions {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  factor: number;
  shouldRetry?: (error: any) => boolean;
}

const DEFAULT_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  factor: 2,
};

function isRateLimitError(err: any): boolean {
  if (!err) return false;
  const status = err.status ?? err.statusCode;
  if (status === 429) return true;
  const msg = (err.message || '').toLowerCase();
  return msg.includes('429') || msg.includes('rate limit') || msg.includes('too many requests');
}

/**
 * Execute a task with exponential backoff and jitter.
 */
export async function withRetry<T>(
  task: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const opt = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;
  let delay = opt.initialDelayMs;

  for (let attempt = 0; attempt <= opt.maxRetries; attempt++) {
    try {
      return await task();
    } catch (err) {
      lastError = err;

      if (opt.shouldRetry && !opt.shouldRetry(err)) {
        throw err;
      }

      if (attempt === opt.maxRetries) {
        break;
      }

      const is429 = isRateLimitError(err);
      // For 429 rate limits, enforce minimum 5s backoff and increase delay quickly
      const baseDelay = is429 ? Math.max(delay, 5000) : delay;
      // Add random jitter (80% - 120%) to avoid synchronized retry stampedes
      const jitter = 0.8 + Math.random() * 0.4;
      const actualDelay = Math.min(Math.round(baseDelay * jitter), opt.maxDelayMs);

      console.warn(`[retry] Task failed (attempt ${attempt + 1}/${opt.maxRetries + 1}), retrying in ${actualDelay}ms...`, err);

      await new Promise(resolve => setTimeout(resolve, actualDelay));
      delay = Math.min(baseDelay * opt.factor, opt.maxDelayMs);
    }
  }

  throw lastError;
}
